import { useState, useEffect, useRef } from "react";
import selectors from "selectors";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatientDocuments,
  fetchDocumentCategories
} from "actions/documents";
import { Page, Modal } from "components/ui";
import DocumentItem from "./document";
import UploadPatientDocumentForm from "./upload-document-form";
import { Button } from "components/ui_new/index";
import "./documents.scss";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import { useKeyboardNavigation } from "./use-keyboard-navigation";
import BreadCrumbs from "components/ui_new/breadcrumbs";

const Documents = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const documents = useSelector(selectors.getPatientDocuments);
  const [isFetching, setIsFetching] = useState(true);

  // Set document title for accessibility
  useDocumentTitle(translate("myDocuments"));

  const uploadButtonRef = useRef(null);

  const { handleKeyDown, focusUploadButton } = useKeyboardNavigation({
    uploadButtonRef
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        await dispatch(fetchPatientDocuments());
        await dispatch(fetchDocumentCategories());
      } finally {
        setIsFetching(false);
      }
    };
    loadData();
  }, [dispatch]);

  return (
    <Page.Container>
      <Page.Header title={translate("myDocuments")} goBack />
      <div className="documents-screen">
        <div className="documents-header" tabIndex={-1}>
          <BreadCrumbs
            childLabel="myDocuments"
            parentLink="/documents"
            parentLinkLabel="documents"
            title="myDocuments"
            titleClassname="document-title-main"
          />
          <Modal
            renderTrigger={({ openModal }) => (
              <Button
                id="upload-document-button"
                ref={uploadButtonRef}
                variant="contained"
                type="submit"
                onClick={openModal}
                onKeyDown={handleKeyDown}
                aria-controls="upload-document-modal"
                aria-label={`${translate("uploadDocument")} - ${translate(
                  "opensModal"
                )}`}
                aria-haspopup="dialog"
                tabIndex={0}
              >
                {translate("uploadDocument")}
              </Button>
            )}
            renderContent={UploadPatientDocumentForm}
          />
        </div>

        <Page.Content
          isEmpty={documents.length === 0}
          emptyMessage={translate("noDocuments")}
          isFetching={isFetching}
        >
          <div
            className="documents-list"
            role="list"
            aria-label={translate("documentsList")}
          >
            {documents.map(doc => (
              <div
                key={doc.document_id}
                style={{ marginRight: "4em" }}
                role="listitem"
              >
                <DocumentItem
                  key={doc.document_id}
                  {...doc}
                  tabIndex={0}
                  onKeyDown={handleKeyDown}
                />
              </div>
            ))}
          </div>
        </Page.Content>
      </div>
    </Page.Container>
  );
};

export default Documents;
